import { initials } from './workbench';

/** Map assignments by `process_node_id:functional_role_id`. */
export function indexAssignments(assignments) {
  const map = new Map();
  for (const a of assignments || []) {
    if (!a.is_active || !a.process_node_id) continue;
    map.set(`${a.process_node_id}:${a.functional_role_id}`, a);
  }
  return map;
}

export function nodePath(nodeId, nodesById) {
  const parts = [];
  let cur = nodesById.get(nodeId);
  while (cur) {
    parts.unshift(cur.name);
    cur = cur.parent_id ? nodesById.get(cur.parent_id) : null;
  }
  return parts.join(' › ');
}

/** Nearest-ancestor wins (same logic as v_effective_bpo). */
export function getEffectiveAssignment(nodeId, roleTypeId, assignmentIndex, nodesById) {
  let cur = nodesById.get(nodeId);
  let depth = 0;
  while (cur) {
    const hit = assignmentIndex.get(`${cur.id}:${roleTypeId}`);
    if (hit) {
      return { assignment: hit, direct: depth === 0, fromNode: cur, depth };
    }
    cur = cur.parent_id ? nodesById.get(cur.parent_id) : null;
    depth += 1;
  }
  return null;
}

/** True when profile can log in and act on routed tasks (§6.0 / §22.4.6). */
export function profileCanActOnTasks(profile) {
  return !!profile?.auth_subject;
}

/** PM-fallback gap: no assignment, or assignee is Vacant/Named/Invited-pending. */
export function assignmentHasResourcingGap(assignment, profilesById) {
  if (!assignment) return true;
  const p = profilesById?.get?.(assignment.profile_id) ?? profilesById?.[assignment.profile_id];
  return !profileCanActOnTasks(p);
}

export function nodeHasGap(nodeId, roleTypes, assignmentIndex, nodesById, profilesById) {
  return roleTypes.some((rt) => {
    const eff = getEffectiveAssignment(nodeId, rt.id, assignmentIndex, nodesById);
    return assignmentHasResourcingGap(eff?.assignment, profilesById);
  });
}

export function profileMatchesRole(profile, role) {
  const label = (profile?.job_title || profile?.full_name || '').toLowerCase();
  const names = [role?.full_name, role?.name].filter(Boolean).map((n) => n.toLowerCase());
  return names.some((roleName) =>
    label.includes(roleName)
    || roleName.split(' ').some((w) => w.length > 3 && label.includes(w))
  );
}

export function sortProfilesForRole(profiles, role) {
  const matching = [];
  const other = [];
  for (const p of profiles) {
    if (profileMatchesRole(p, typeof role === 'string' ? { full_name: role } : role)) matching.push(p);
    else other.push(p);
  }
  return { matching, other };
}

/** BPO-only opt-in workflow rights (§22.4.10). */
export function isBpoRole(role) {
  return role?.full_name === 'Business Process Owner' || role?.name === 'BPO';
}

export const BPO_OPT_IN_RIGHTS = [
  {
    key: 'right_fit_gap',
    label: 'Fit-gap decisions',
    hint: 'This BPO can decide fit-gap at this node when enabled.',
  },
  {
    key: 'right_stage_gate_reviewer',
    label: 'Stage gate reviewer',
    hint: 'This BPO can act as stage gate reviewer at this node when enabled.',
  },
];

export { initials };
